/**
 * LocationSummary Component (Cluster-based)
 *
 * Shows organization's clusters with their contained locations.
 * Clusters are org-created groupings of regions, countries, and/or locations.
 * They're optional (#220): an org without clusters gets a single synthetic
 * "All Locations" group over its tenancy-visible locations. An org WITH
 * clusters additionally gets an "Other Locations" group so active sites no
 * cluster covers — notably the shared locations its tests actually run at —
 * are never hidden by the grouping.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  ChevronDown,
  ChevronRight,
  MapPin,
  Smartphone,
  AlertTriangle,
  ExternalLink,
  Plus,
} from 'lucide-react';
import { Card, Spinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import { db } from '@/config/firebase';
import { getVisibleDeviceDocs } from '@/lib/visibleDevices';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import {
  getClustersForOrg,
  resolveClusterToLocations,
  type ResolvedLocation,
} from '@/services/clusterService';
import {
  getTimeRangeDates,
  type TimeRange,
} from '@/components/charts/TimeRangeSelector';
import type { Region, Country, Location } from '@simcheck/shared-types';
import { isInternalExecution } from '@simcheck/shared-types';

// Sentinel group id used when the org has no clusters (#220)
const ALL_LOCATIONS_GROUP_ID = 'all-locations';
// Sentinel group id for active locations no cluster covers
const UNGROUPED_GROUP_ID = 'other-locations';
// Synthetic groups earn their place only when they have active locations;
// real (user-created) clusters are kept even when empty.
const SYNTHETIC_GROUP_IDS = new Set<string>([ALL_LOCATIONS_GROUP_ID, UNGROUPED_GROUP_ID]);

interface ClusterStats {
  clusterId: string;
  clusterName: string;
  description?: string;
  locationCount: number;
  modemCount: number;
  executionsToday: number;
  successRate: number;
  hasIssues: boolean;
  locations: ResolvedLocation[];
}

interface LocationSummaryProps {
  defaultExpanded?: boolean;
  /** Dashboard's display time frame; scopes the test-run window. Defaults to "today". */
  timeRange?: TimeRange;
}

export const LocationSummary: React.FC<LocationSummaryProps> = ({
  defaultExpanded = false,
  timeRange,
}) => {
  const navigate = useNavigate();
  const { activeOrganization, isSuperAdmin } = useAuth();

  const [clusters, setClusters] = useState<ClusterStats[]>([]);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClusterStats = async () => {
      if (!activeOrganization?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Load clusters for this organization (optional grouping — a
        // cluster-less org falls through to an "All Locations" group, #220)
        const orgClusters = await getClustersForOrg(activeOrganization.id);

        // Load reference data for resolving cluster items
        // Get ALL locations since organizationIds might not be set on all locations
        const [regionsSnap, countriesSnap, locationsSnap, onlineDevicesSnap] = await Promise.all([
          getDocs(collection(db, 'regions')),
          getDocs(collection(db, 'countries')),
          getDocs(collection(db, 'locations')),
          getVisibleDeviceDocs(db, activeOrganization.id, isSuperAdmin, [where('status', '==', 'online')]),
        ]);

        const regions = regionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Region[];
        const countries = countriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Country[];
        // Tenancy (#202): never surface other orgs' private locations.
        const locations = filterLocationsByTenancy(
          locationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Location[],
          activeOrganization.id,
          isSuperAdmin
        );

        // Live modem counts per location, summed across ONLINE devices only.
        // Offline devices contribute 0, so a location that temporarily drops
        // can still be kept by its recent test runs (below) instead of vanishing.
        const liveModemCountByLocation = new Map<string, number>();
        onlineDevicesSnap.forEach((deviceDoc) => {
          const data = deviceDoc.data() as any;
          const locationId = data.locationId;
          if (!locationId) return;
          const count = Array.isArray(data.modems)
            ? data.modems.length
            : (data.health?.modemCount ?? 0);
          liveModemCountByLocation.set(
            locationId,
            (liveModemCountByLocation.get(locationId) ?? 0) + count
          );
        });

        // Executions within the dashboard's display time frame (defaults to today)
        const { start: rangeStart } = timeRange
          ? getTimeRangeDates(timeRange)
          : (() => {
              const s = new Date();
              s.setHours(0, 0, 0, 0);
              return { start: s };
            })();

        const executionsQuery = query(
          collection(db, 'executions'),
          where('organizationId', '==', activeOrganization.id),
          where('startedAt', '>=', Timestamp.fromDate(rangeStart))
        );
        const executionsSnapshot = await getDocs(executionsQuery);
        // Platform-operated runs aren't customer tests — they must not inflate
        // a location's test count or keep a location on the map (#267).
        const executions = executionsSnapshot.docs
          .map((doc) => doc.data())
          .filter((e) => !isInternalExecution(e));

        // Test-run counts per location within the time frame (any status)
        const execCountByLocation = new Map<string, number>();
        executions.forEach((exec: any) => {
          if (exec.locationId) {
            execCountByLocation.set(
              exec.locationId,
              (execCountByLocation.get(exec.locationId) ?? 0) + 1
            );
          }
        });

        const toResolved = (loc: Location): ResolvedLocation => {
          const country = countries.find((c) => c.id === loc.countryId);
          const region = regions.find((r) => r.id === loc.regionId);
          return {
            locationId: loc.id,
            locationName: loc.displayName || loc.name || 'Unknown',
            countryId: loc.countryId || '',
            countryName: country?.name || 'Unknown',
            regionId: loc.regionId || '',
            regionName: region?.name || 'Unknown',
            modemCount: loc.modemCount || 0,
          };
        };

        // Groups to render: the org's clusters, or — when it has none — a
        // single synthetic "All Locations" group over its visible locations.
        const groups: Array<{
          id: string;
          name: string;
          description?: string;
          resolved: ResolvedLocation[];
        }> =
          orgClusters.length > 0
            ? await Promise.all(
                orgClusters.map(async (cluster) => ({
                  id: cluster.id,
                  name: cluster.name,
                  description: cluster.description || undefined,
                  resolved: await resolveClusterToLocations(
                    cluster.items,
                    regions,
                    countries,
                    locations
                  ),
                }))
              )
            : [
                {
                  id: ALL_LOCATIONS_GROUP_ID,
                  name: 'All Locations',
                  resolved: locations.map(toResolved),
                },
              ];

        // Clusters group locations, they don't scope them. Anything visible and
        // active that no cluster covers gets its own group — otherwise an org
        // whose clusters only list its own sites loses the SHARED locations its
        // tests actually run at (pruned below to active ones, so this stays empty
        // for an org whose clusters already cover everything live).
        if (orgClusters.length > 0) {
          const clustered = new Set(
            groups.flatMap((g) => g.resolved.map((loc) => loc.locationId))
          );
          groups.push({
            id: UNGROUPED_GROUP_ID,
            name: 'Other Locations',
            description: 'Active locations not in any cluster',
            resolved: locations.filter((loc) => !clustered.has(loc.id)).map(toResolved),
          });
        }

        // Build per-group stats
        const clusterStats: ClusterStats[] = groups.map((group) => {
            // Stamp each location with its live modem count
            const resolvedLocations = group.resolved.map((loc) => ({
              ...loc,
              modemCount: liveModemCountByLocation.get(loc.locationId) ?? 0,
            }));

            // Keep a location only if it currently has modems OR had test runs in
            // the time frame. Hides empty/demo seed locations while preserving any
            // location that merely went down temporarily (kept by its run history).
            const visibleLocations = resolvedLocations.filter(
              (loc) =>
                loc.modemCount > 0 ||
                (execCountByLocation.get(loc.locationId) ?? 0) > 0
            );

            // Calculate modem counts
            const modemCount = visibleLocations.reduce(
              (sum, loc) => sum + (loc.modemCount || 0),
              0
            );

            // Calculate execution stats for this cluster's visible locations
            const locationIds = new Set(visibleLocations.map(loc => loc.locationId));
            const clusterExecutions = executions.filter(
              (exec: any) => exec.locationId && locationIds.has(exec.locationId)
            );

            const successfulExecutions = clusterExecutions.filter(
              (exec: any) => exec.status === 'completed'
            ).length;
            const failedExecutions = clusterExecutions.filter(
              (exec: any) => exec.status === 'failed'
            ).length;
            const totalCompleted = successfulExecutions + failedExecutions;

            const successRate =
              totalCompleted > 0
                ? Math.round((successfulExecutions / totalCompleted) * 100)
                : 100;

            return {
              clusterId: group.id,
              clusterName: group.name,
              description: group.description,
              locationCount: visibleLocations.length,
              modemCount,
              executionsToday: clusterExecutions.length,
              successRate,
              hasIssues: failedExecutions > 0 || successRate < 80,
              locations: visibleLocations,
            };
        });

        const finalStats = clusterStats.filter(
          (c) => !SYNTHETIC_GROUP_IDS.has(c.clusterId) || c.locationCount > 0
        );

        // Sort by location count (most first)
        finalStats.sort((a, b) => b.locationCount - a.locationCount);

        setClusters(finalStats);

        // Expand clusters with issues by default; synthetic groups (all-locations,
        // other-locations) hold the live sites, so always expand them
        if (defaultExpanded) {
          setExpandedClusters(new Set(finalStats.map((c) => c.clusterId)));
        } else {
          const expandedIds = finalStats
            .filter((c) => c.hasIssues || SYNTHETIC_GROUP_IDS.has(c.clusterId))
            .map((c) => c.clusterId);
          setExpandedClusters(new Set(expandedIds));
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading cluster stats:', err);
        setLoading(false);
      }
    };

    loadClusterStats();

    // Refresh every 60 seconds
    const interval = setInterval(loadClusterStats, 60000);
    return () => clearInterval(interval);
  }, [
    activeOrganization?.id,
    defaultExpanded,
    timeRange?.preset,
    timeRange?.hours,
    timeRange?.startDate?.getTime(),
    timeRange?.endDate?.getTime(),
  ]);

  const toggleCluster = (clusterId: string) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        next.delete(clusterId);
      } else {
        next.add(clusterId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-2 text-muted-foreground">Loading clusters...</span>
        </div>
      </Card>
    );
  }

  const totalLocations = clusters.reduce((sum, c) => sum + c.locationCount, 0);
  const totalModems = clusters.reduce((sum, c) => sum + c.modemCount, 0);

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-h3 font-semibold text-foreground">
            Global Locations
          </h3>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {totalLocations}
          </span>
          <span className="flex items-center gap-1">
            <Smartphone className="w-4 h-4" />
            {totalModems}
          </span>
        </div>
      </div>

      {clusters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
          <Layers className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">No active locations</p>
          <button
            onClick={() => navigate('/edges')}
            className="text-sm text-accent-blue hover:text-accent-blue/80 mt-2 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Manage locations
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {clusters.map((cluster) => {
            const isExpanded = expandedClusters.has(cluster.clusterId);

            return (
              <div
                key={cluster.clusterId}
                className="border border-border rounded-lg overflow-hidden"
              >
                {/* Cluster Header */}
                <button
                  onClick={() => toggleCluster(cluster.clusterId)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}

                  <span className="font-medium text-foreground flex-1">
                    {cluster.clusterName}
                  </span>

                  {cluster.hasIssues && (
                    <AlertTriangle className="w-4 h-4 text-status-warning" />
                  )}

                  <span className="text-sm text-muted-foreground">
                    {cluster.locationCount} location{cluster.locationCount !== 1 ? 's' : ''}
                  </span>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0">
                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 bg-muted/50 rounded mb-2">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-foreground">
                          {cluster.locationCount}
                        </div>
                        <div className="text-xs text-muted-foreground">Locations</div>
                      </div>

                      <div className="text-center">
                        <div className="text-lg font-semibold text-foreground">
                          {cluster.modemCount}
                        </div>
                        <div className="text-xs text-muted-foreground">Modems</div>
                      </div>

                      <div className="text-center">
                        <div className="text-lg font-semibold text-foreground">
                          {cluster.executionsToday}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Tests{timeRange?.label ? ` (${timeRange.label})` : ''}
                        </div>
                      </div>

                      <div className="text-center">
                        <div
                          className={`text-lg font-semibold ${
                            cluster.successRate >= 90
                              ? 'text-status-success'
                              : cluster.successRate >= 70
                              ? 'text-status-warning'
                              : 'text-status-error'
                          }`}
                        >
                          {cluster.successRate}%
                        </div>
                        <div className="text-xs text-muted-foreground">Success Rate</div>
                      </div>
                    </div>

                    {/* Locations List */}
                    {cluster.locations.length > 0 && (
                      <div className="space-y-1">
                        {cluster.locations.slice(0, 5).map((loc) => (
                          <div
                            key={loc.locationId}
                            className="flex items-center gap-2 text-xs text-muted-foreground py-1 px-2"
                          >
                            <MapPin className="w-3 h-3" />
                            <span className="flex-1 truncate">{loc.locationName}</span>
                            <span className="text-muted-foreground/70">{loc.countryName}</span>
                          </div>
                        ))}
                        {cluster.locations.length > 5 && (
                          <div className="text-xs text-muted-foreground/70 px-2 py-1">
                            +{cluster.locations.length - 5} more locations
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View all link */}
      {clusters.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <button
            onClick={() => navigate('/edges')}
            className="text-sm text-accent-blue hover:text-accent-blue/80 transition-colors flex items-center gap-1"
          >
            {clusters.some((c) => c.clusterId !== ALL_LOCATIONS_GROUP_ID)
              ? 'Manage clusters'
              : 'Manage locations'}
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}
    </Card>
  );
};

export default LocationSummary;
